import { cn, useTranslation } from '@hbs-us/ui';
import { Link } from '@tanstack/react-router';

import type { EmployeeStatus } from '../contractsMirror.js';
import type { EmployeesSearch } from '../searchParams.js';

/**
 * The active/archived status-filter pill row on the employees list (legacy
 * `EmployeesPage` active-vs-archived toggle, surfaced as the cluster-wide pill
 * pattern). Clones the estimates `StatusFilterPills`: each pill is a `<Link>` that
 * flips `?show_archived` in the typed search params, driving exactly one
 * coarse-endpoint refetch. Each pill shows its SERVER-computed count (22-05
 * `statusCounts` / `totalEmployees`, filter-independent so the counts stay stable
 * while a filter is active). The active pill is the one place the orange accent is
 * used here (`bg-primary text-primary-foreground`); 44px targets; pills wrap (ES
 * layout obligation, I18N-05). Counts in IBM Plex Mono.
 */

const PILL_BASE =
  'inline-flex h-11 items-center gap-2 rounded-md border px-4 font-medium text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';
const PILL_ACTIVE = 'border-transparent bg-primary text-primary-foreground';
const PILL_INACTIVE = 'border-border bg-card text-foreground hover:bg-secondary';

/** Preserve the active text search when flipping the archived pill (deep-link parity). */
function pillSearch(search: EmployeesSearch, showArchived: boolean): EmployeesSearch {
  const next: EmployeesSearch = {};
  if (search.q) next.q = search.q;
  if (showArchived) next.show_archived = true;
  return next;
}

export function StatusFilterPills({
  search,
  statusCounts,
}: {
  search: EmployeesSearch;
  statusCounts: Partial<Record<EmployeeStatus, number>>;
}) {
  const { t } = useTranslation();
  const showArchived = Boolean(search.show_archived);
  const activeCount = statusCounts.active ?? 0;
  const archivedCount = statusCounts.archived ?? 0;

  return (
    <section
      aria-label={t('employees:filter.regionLabel')}
      className="mb-4 rounded-md border border-border bg-card p-6"
    >
      <h2 className="mb-3 font-semibold text-foreground text-sm">{t('employees:filter.heading')}</h2>
      <div className="flex flex-wrap gap-2">
        <Link
          to="/employees"
          search={pillSearch(search, false)}
          aria-current={showArchived ? undefined : 'true'}
          className={cn(PILL_BASE, showArchived ? PILL_INACTIVE : PILL_ACTIVE)}
        >
          {t('employees:filter.active')}
          <span className="font-mono text-xs opacity-80">{activeCount}</span>
        </Link>
        <Link
          to="/employees"
          search={pillSearch(search, true)}
          aria-current={showArchived ? 'true' : undefined}
          className={cn(PILL_BASE, showArchived ? PILL_ACTIVE : PILL_INACTIVE)}
        >
          {t('employees:filter.archived')}
          <span className="font-mono text-xs opacity-80">{archivedCount}</span>
        </Link>
      </div>
    </section>
  );
}
